import { round, identity } from 'lodash';

import { convertValue, getUnitDisplay } from '~/utils/convert';

const DEFAULT_PRECISION = 2;

export const getSum = ({ sumVal }) => sumVal;
export const getAverage = ({ sumVal, number }) => sumVal / number;
export const getMax = ({ maxVal }) => maxVal;

/**
 * Metrics-config specific unit display.
 * Retrieves the unit display name for a given unit type
 * @param  {Function} formatUnitDisplay=identity
 * @param  {String} to
 * @param  {String} unitType
 */
export function getMetricUnitDisplay({ formatUnitDisplay = identity, to, unitType } = {}) {
  // NOTE: If no unitType provided - show `to` value as unitDisplay
  if (!to && !unitType) return '';
  return formatUnitDisplay(to || getUnitDisplay(unitType));
}

/**
 * Converts a single value. Metrics-config specific.
 * @param  {Number} metricApiValue
 * @param  {Object<{
 *    unitType: String,
 *    from: String,
 *    to: String,
 *    customConvert: Function,
 *    convertForMultiRigs: Function,
 *    precision: Number,
 *    allowNegativeValue: Boolean
 * }>} metricConfig
 * @returns {Number}
 */
export function getConvertedMetricValue(metricApiValue, metricConfig, rigsNum, metricsMaxValue) {
  // NOTE: Metric api value must be numeric
  if (!Number.isFinite(metricApiValue) || !metricConfig) return null;

  const {
    unitType, // The class of unit such as volume, length, mass, etc.
    from, // The specific unit such as m, gal, lb, etc. API returns metric in this unit
    to, // To (optional) the unit that we want to convert the value to.
    customConvert, // Use custom conversion if provided
    convertForMultiRigs, // Use custom conversion for multiple rigs if provided
    precision = 2, // Round metric with this precision
    allowNegativeValue = false,
  } = metricConfig;

  const convertedValueForMultiRigs =
    convertForMultiRigs && rigsNum
      ? convertForMultiRigs({ sumVal: metricApiValue, maxVal: metricsMaxValue, number: rigsNum })
      : metricApiValue;
  if (customConvert) return customConvert(convertedValueForMultiRigs, metricConfig);

  // NOTE: Scale, convert and round value
  let value = unitType
    ? convertValue(convertedValueForMultiRigs, unitType, from, to, precision)
    : convertedValueForMultiRigs;
  // NOTE: Convert's round sometimes skip rounding
  value = round(value, precision);

  // NOTE: Metrics should be non-negative by default
  value = allowNegativeValue ? value : Math.max(value, 0);
  return value;
}

/**
 * Round down all values between 100-101.50% to 100.00%. Leave all values above 101.50%.
 * @param  {Number} value
 * @param  {Object<{precision: Number}>} precision
 * @returns {Number}
 */
export function convertPercentageMetrics(value, { precision = DEFAULT_PRECISION } = {}) {
  const convertedValue = value * 100;
  return round(convertedValue > 100 && convertedValue < 101.5 ? 100 : convertedValue, precision);
}

/**
 * Converts value from percents to absolute value
 * @param {Number} value
 * @returns {Number}
 */
export function convertPercentageToSave(value) {
  return Number(value) / 100;
}

/**
 * Converts cost value to show on the UI
 * @param {Number} value
 * @returns {Number}
 */
export function convertCostToShow(value) {
  return value / convertValue(1, 'length', 'ft');
}

/**
 * Converts cost value to save
 * @param {Number} value
 * @returns {Number}
 */
export function convertCostToSave(value) {
  return value * convertValue(1, 'length', 'ft');
}
